import React, { useState, useEffect } from 'react';
import { Scenario } from '../../types/sop';
import TimeSeriesChart from './TimeSeriesChart';
import LoadingIndicator from '../common/LoadingIndicator';
import './WhatIfAnalysis.css';

interface WhatIfAnalysisProps {
  scenarioId: string;
  scenario?: Scenario;
  onBack: () => void;
}

interface WhatIfParameter {
  key: string;
  label: string;
  currentValue: number;
  minValue: number;
  maxValue: number;
  step: number;
  unit: string;
  description: string;
}

interface WhatIfResult {
  parameter: string;
  value: number;
  impact: {
    total_inventory: number;
    stockout_risk: number;
    total_cost: number;
    service_level: number;
  };
}

const WhatIfAnalysis: React.FC<WhatIfAnalysisProps> = ({
  scenarioId,
  scenario,
  onBack
}) => {
  const [parameters, setParameters] = useState<WhatIfParameter[]>([]);
  const [results, setResults] = useState<WhatIfResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<string>('');

  useEffect(() => {
    initializeParameters();
  }, [scenario]);

  useEffect(() => {
    if (selectedParameter) {
      runWhatIfAnalysis();
    }
  }, [selectedParameter]);

  const initializeParameters = () => {
    if (!scenario) return;

    const defaultParameters: WhatIfParameter[] = [
      {
        key: 'demand_growth_rate',
        label: 'Demand Growth Rate',
        currentValue: scenario.assumptions.demand_growth_rate || 0.1,
        minValue: -0.2,
        maxValue: 0.5,
        step: 0.05,
        unit: '%',
        description: 'Expected growth in customer demand'
      },
      {
        key: 'supply_constraint_factor',
        label: 'Supply Constraint',
        currentValue: scenario.assumptions.supply_constraint_factor || 1.0,
        minValue: 0.5,
        maxValue: 1.2,
        step: 0.1,
        unit: 'x',
        description: 'Multiplier for supply availability'
      },
      {
        key: 'lead_time_variance',
        label: 'Lead Time Variance',
        currentValue: scenario.assumptions.lead_time_variance || 1.0,
        minValue: 0.5,
        maxValue: 2.0,
        step: 0.1,
        unit: 'x',
        description: 'Variability in supplier lead times'
      },
      {
        key: 'safety_stock_multiplier',
        label: 'Safety Stock Level',
        currentValue: scenario.assumptions.safety_stock_multiplier || 1.0,
        minValue: 0.5,
        maxValue: 3.0,
        step: 0.25,
        unit: 'x',
        description: 'Safety stock as multiple of average demand'
      }
    ];

    setParameters(defaultParameters);
    setSelectedParameter(defaultParameters[0].key);
  };

  const runWhatIfAnalysis = async () => {
    if (!selectedParameter) return;

    setLoading(true);
    try {
      const parameter = parameters.find(p => p.key === selectedParameter);
      if (!parameter) return;

      // Simulate what-if analysis by generating results for different parameter values
      const analysisResults: WhatIfResult[] = [];
      const steps = 10;
      const stepSize = (parameter.maxValue - parameter.minValue) / steps;

      for (let i = 0; i <= steps; i++) {
        const value = parameter.minValue + (i * stepSize);
        
        // Simulate impact calculation based on parameter changes
        const impact = calculateImpact(parameter.key, value, parameter.currentValue);
        
        analysisResults.push({
          parameter: parameter.key,
          value,
          impact
        });
      }

      setResults(analysisResults);
    } catch (err) {
      console.error('What-if analysis failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateImpact = (parameterKey: string, newValue: number, currentValue: number) => {
    // Simplified impact calculation - in real implementation, this would call the backend
    const changeFactor = newValue / currentValue;
    
    let inventoryImpact = 0;
    let stockoutImpact = 0;
    let costImpact = 0;
    let serviceImpact = 0;

    switch (parameterKey) {
      case 'demand_growth_rate':
        inventoryImpact = -changeFactor * 1000; // Higher demand = lower inventory
        stockoutImpact = changeFactor * 0.1; // Higher demand = higher stockout risk
        costImpact = changeFactor * 5000; // Higher demand = higher costs
        serviceImpact = -changeFactor * 0.05; // Higher demand = lower service level
        break;
      
      case 'supply_constraint_factor':
        inventoryImpact = changeFactor * 800; // Better supply = higher inventory
        stockoutImpact = -changeFactor * 0.08; // Better supply = lower stockout risk
        costImpact = -changeFactor * 3000; // Better supply = lower costs
        serviceImpact = changeFactor * 0.04; // Better supply = better service
        break;
      
      case 'lead_time_variance':
        inventoryImpact = changeFactor * 500; // Higher variance = more safety stock
        stockoutImpact = changeFactor * 0.06; // Higher variance = higher stockout risk
        costImpact = changeFactor * 2000; // Higher variance = higher costs
        serviceImpact = -changeFactor * 0.03; // Higher variance = lower service
        break;
      
      case 'safety_stock_multiplier':
        inventoryImpact = changeFactor * 1200; // More safety stock = higher inventory
        stockoutImpact = -changeFactor * 0.12; // More safety stock = lower stockout risk
        costImpact = changeFactor * 4000; // More safety stock = higher costs
        serviceImpact = changeFactor * 0.06; // More safety stock = better service
        break;
    }

    return {
      total_inventory: Math.max(0, 10000 + inventoryImpact),
      stockout_risk: Math.max(0, Math.min(1, 0.1 + stockoutImpact)),
      total_cost: Math.max(0, 50000 + costImpact),
      service_level: Math.max(0, Math.min(1, 0.95 + serviceImpact))
    };
  };

  const updateParameterValue = (parameterKey: string, newValue: number) => {
    setParameters(prev => prev.map(p => 
      p.key === parameterKey ? { ...p, currentValue: newValue } : p
    ));
  };

  const formatValue = (value: number, unit: string) => {
    if (unit === '%') {
      return `${(value * 100).toFixed(1)}%`;
    } else if (unit === 'x') {
      return `${value.toFixed(2)}x`;
    } else {
      return value.toLocaleString();
    }
  };

  const generateChartData = (metric: string) => {
    return results.map(result => ({
      week_date: formatValue(result.value, parameters.find(p => p.key === selectedParameter)?.unit || ''),
      total_inbound: 0,
      total_outbound: 0,
      total_inventory: result.impact[metric as keyof typeof result.impact] || 0,
      warehouse_count: 1
    }));
  };

  const getMetricColor = (metric: string) => {
    const colors = {
      total_inventory: '#2196F3',
      stockout_risk: '#F44336',
      total_cost: '#9C27B0',
      service_level: '#4CAF50'
    };
    return colors[metric as keyof typeof colors] || '#757575';
  };

  if (!scenario) {
    return (
      <div className="whatif-analysis">
        <div className="error-message">
          <h3>Scenario not found</h3>
          <button onClick={onBack} className="btn btn-secondary">
            Back to Scenarios
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="whatif-analysis">
      <div className="whatif-header">
        <h2>What-If Analysis</h2>
        <p>Analyze the impact of changing key parameters in "{scenario.scenario_name}"</p>
      </div>

      <div className="whatif-content">
        <div className="parameter-controls">
          <h3>Adjust Parameters</h3>
          
          <div className="parameter-selector">
            <label htmlFor="parameter-select">Select Parameter:</label>
            <select
              id="parameter-select"
              value={selectedParameter}
              onChange={(e) => setSelectedParameter(e.target.value)}
              className="form-select"
            >
              {parameters.map(param => (
                <option key={param.key} value={param.key}>
                  {param.label}
                </option>
              ))}
            </select>
          </div>

          {parameters.map(param => (
            <div key={param.key} className={`parameter-control ${param.key === selectedParameter ? 'active' : ''}`}>
              <div className="parameter-info">
                <h4>{param.label}</h4>
                <p>{param.description}</p>
              </div>
              
              <div className="parameter-slider">
                <input
                  type="range"
                  min={param.minValue}
                  max={param.maxValue}
                  step={param.step}
                  value={param.currentValue}
                  onChange={(e) => updateParameterValue(param.key, parseFloat(e.target.value))}
                  className="slider"
                />
                <div className="slider-labels">
                  <span>{formatValue(param.minValue, param.unit)}</span>
                  <span className="current-value">
                    {formatValue(param.currentValue, param.unit)}
                  </span>
                  <span>{formatValue(param.maxValue, param.unit)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="analysis-results">
          <h3>Impact Analysis</h3>
          
          {loading ? (
            <LoadingIndicator message="Running what-if analysis..." />
          ) : (
            <>
              <div className="results-charts">
                {['total_inventory', 'stockout_risk', 'total_cost', 'service_level'].map(metric => {
                  const selectedParam = parameters.find(p => p.key === selectedParameter);
                  return (
                    <div key={metric} className="chart-container">
                      <h4>
                        {metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                        vs {selectedParam?.label}
                      </h4>
                      <TimeSeriesChart
                        data={generateChartData(metric)}
                        metrics={['total_inventory']}
                        colors={[getMetricColor(metric)]}
                        title={metric}
                        width={400}
                        height={250}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="sensitivity-summary">
                <h4>Sensitivity Summary</h4>
                <div className="summary-cards">
                  {results.length > 0 && (
                    <>
                      <div className="summary-card">
                        <h5>Most Sensitive Metric</h5>
                        <p>Stockout Risk</p>
                        <span className="sensitivity-score">High Sensitivity</span>
                      </div>
                      <div className="summary-card">
                        <h5>Optimal Range</h5>
                        <p>{selectedParameter.replace(/_/g, ' ')}</p>
                        <span className="optimal-range">
                          {formatValue(
                            parameters.find(p => p.key === selectedParameter)?.currentValue || 0,
                            parameters.find(p => p.key === selectedParameter)?.unit || ''
                          )} ± 20%
                        </span>
                      </div>
                      <div className="summary-card">
                        <h5>Risk Assessment</h5>
                        <p>Parameter Changes</p>
                        <span className="risk-level">Medium Risk</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="whatif-actions">
        <button onClick={onBack} className="btn btn-secondary">
          Back to Scenarios
        </button>
        <button 
          onClick={() => {
            // In real implementation, this would save the modified scenario
            alert('Modified scenario parameters saved!');
          }}
          className="btn btn-primary"
        >
          Save Modified Scenario
        </button>
      </div>
    </div>
  );
};

export default WhatIfAnalysis;